import { entityExists, hasComponent, query } from 'bitecs';
import { Config } from '@src/core/Config';
import { difficulty, formatContinues } from '@src/core/Difficulty';
import { GameEvent } from '@src/core/EventQueue';
import { PoolKind } from '@src/core/Pools';
import { BossTag, Dying, Health, Player, Position, Spawner, Weapon } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { BossState, PopupKind, POPUP_STRIDE, RoomPhase, type GameContext } from '@src/ecs/world';
import { DoorKind, DOOR_NAMES } from '@src/game/arena/RoomLayout';
import { bossDef } from '@src/game/BossDefs';
import { isFinalFloor } from '@src/game/arena/FloorMap';
import { FloorModifier, floorMod } from '@src/game/FloorModifiers';
import { floorThemeDef, hexCss } from '@src/game/arena/FloorThemes';
import { smallFryPackName } from '@src/game/SpawnRoster';
import { WeaponId, weaponDef } from '@src/game/weapons/WeaponDefs';

const GATE_LABELS = ['N', 'E', 'S', 'W'];
const POPUP_POOL = 24;
const POPUP_LIFE_MS = 900;
const HEALTH_SEGMENTS = 10;
const PORTAL_HP_POOL = Config.pools.spawners;
const BOSS_SEGMENTS = 12;
const STINGER_MS = 1400;
const STINGER_QUEUE_CAP = 3;
const CONTINUE_MS = 9000;
const CONTINUE_ARM_MS = 550;

interface StingerJob {
  text: string;
  kind: string;
  priority: number;
}

type Projector = (wx: number, wy: number, out: Float32Array) => void;

function continueCopy(remaining: number): string {
  if (!Number.isFinite(remaining)) return 'UNLIMITED  //  ENTER / A / TAP CONTINUE';
  if (remaining <= 1) return 'LAST CONTINUE  //  ENTER / A / TAP CONTINUE';
  return `${remaining} LEFT  //  ENTER / A / TAP CONTINUE`;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, parent: HTMLElement, text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  parent.appendChild(node);
  return node;
}

interface Popup {
  node: HTMLElement;
  until: number;
  x: number;
  y: number;
}

/**
 * DOM overlay: segmented health, weapon timer, rolling score, combo counter,
 * floor minimap (zooms to center during transitions), floating score popups,
 * center stingers, warnings, captions, and end screens.
 */
export class Hud implements GameSystem {
  private ctx!: GameContext;
  private readonly root: HTMLElement;
  private healthSegs: HTMLElement[] = [];
  private continuesTag!: HTMLElement;
  private healthText!: HTMLElement;
  private healthFill!: HTMLElement;
  private powerWarning!: HTMLElement;
  private worldHp!: HTMLElement;
  private worldHpFill!: HTMLElement;
  private worldWarning!: HTMLElement;
  private readonly portalHp: { node: HTMLElement; fill: HTMLElement; num: HTMLElement }[] = [];
  private weaponName!: HTMLElement;
  private weaponBar!: HTMLElement;
  private weaponFill!: HTMLElement;
  private buffs!: HTMLElement;
  private buffShield!: HTMLElement;
  private buffShieldTime!: HTMLElement;
  private buffShieldFill!: HTMLElement;
  private buffSwift!: HTMLElement;
  private buffSwiftTime!: HTMLElement;
  private buffSwiftFill!: HTMLElement;
  private scoreValue!: HTMLElement;
  private comboBox!: HTMLElement;
  private comboValue!: HTMLElement;
  private comboMult!: HTMLElement;
  private roomValue!: HTMLElement;
  private roomSub!: HTMLElement;
  private tierTag!: HTMLElement;
  private caption!: HTMLElement;
  private captionText!: HTMLElement;
  private warning!: HTMLElement;
  private boss!: HTMLElement;
  private bossName!: HTMLElement;
  private bossTitle!: HTMLElement;
  private bossFill!: HTMLElement;
  private bossGhost!: HTMLElement;
  private bossHp!: HTMLElement;
  private bossShownFrac = -1;
  private bossGhostFrac = 1;
  private bossKindShown = -1;
  private bossCard!: HTMLElement;
  private bossCardKicker!: HTMLElement;
  private bossCardName!: HTMLElement;
  private bossCardTitle!: HTMLElement;
  private bossCardUntil = 0;
  private vignette!: HTMLElement;
  private flash!: HTMLElement;
  private stinger!: HTMLElement;
  private center!: HTMLElement;
  private centerTitle!: HTMLElement;
  private continueClock!: HTMLElement;
  private continueMeter!: HTMLElement;
  private continueMeterFill!: HTMLElement;
  private centerSub!: HTMLElement;
  private centerDoors!: HTMLElement;
  private centerButton!: HTMLButtonElement;
  private bottomLeft!: HTMLElement;
  private minimap!: HTMLElement;
  private minimapGrid!: HTMLElement;
  private minimapLabel!: HTMLElement;
  private minimapTheme!: HTMLElement;
  private minimapRule!: HTMLElement;
  private lastThemeFloor = 0;
  private minimapCells: HTMLElement[] = [];
  /** Grid shape the minimap cells were built for. */
  private minimapCols = 0;
  private minimapRows = 0;
  private popupLayer!: HTMLElement;
  private readonly popups: Popup[] = [];
  private readonly screen = new Float32Array(2);
  private projector: Projector | null = null;
  private captionUntil = 0;
  private hurtUntil = 0;
  private stingerUntil = 0;
  private stingerPriority = 0;
  private readonly stingerQueue: StingerJob[] = [];
  private warningUntil = 0;
  private beginStingerAt = 0;
  private lastPhase: RoomPhase = RoomPhase.Enter;
  private shownScore = 0;
  private lastRoom = -1;
  private lastFeatured = -1;
  private lastDoorSig = '';
  private lastCombo = 0;
  private lastMapSig = '';
  private displayHp = -1;
  onRestart: (() => void) | null = null;
  onTitle: (() => void) | null = null;
  onPause: (() => void) | null = null;
  onContinue: (() => void) | null = null;
  onContinueExpire: (() => void) | null = null;
  private centerTitleBtn!: HTMLButtonElement;
  /** Death cinematic owns the screen; game-over panel waits until release. */
  private holdGameOver = false;
  private continueOpen = false;
  private continueArmed = false;
  private continueAccepted = false;
  private readonly continueTimers = new Set<number>();
  private unbindContinue: () => void = () => {};
  private lastContinues = -1;
  private continueLastSec = -1;

  constructor(root: HTMLElement) {
    this.root = root;
  }

  init(ctx: GameContext): void {
    this.ctx = ctx;
    this.build();
  }

  destroy(): void {
    this.teardownContinue();
    this.root.replaceChildren();
  }

  setProjector(p: Projector): void {
    this.projector = p;
  }

  private build(): void {
    const root = this.root;
    root.replaceChildren();
    this.vignette = el('div', 'hud-vignette', root);
    this.flash = el('div', 'hud-flash', root);
    this.popupLayer = el('div', 'hud-popups', root);
    for (let i = 0; i < POPUP_POOL; i++) {
      const node = el('div', 'hud-popup', this.popupLayer);
      this.popups.push({ node, until: 0, x: 0, y: 0 });
    }

    const top = el('div', 'hud-top', root);
    const left = el('div', 'hud-block hud-player', top);
    const playerHeader = el('div', 'hud-player-header', left);
    const playerLabel = el('div', 'hud-label', playerHeader, 'Player');
    this.continuesTag = el('span', 'hud-continues', playerLabel, 'CONT 0');
    this.powerWarning = el('span', 'hud-power-warning hide', playerHeader, 'LOW POWER');
    const hpRow = el('div', 'hud-hp-row', left);
    const track = el('div', 'hud-hp-track', hpRow);
    this.healthFill = el('i', 'hud-hp-fill', track);
    const hb = el('div', 'hud-segments', track);
    for (let i = 0; i < HEALTH_SEGMENTS; i++) this.healthSegs.push(el('i', 'on', hb));
    this.healthText = el('div', 'hud-hp-num', hpRow, '100 / 100');

    this.worldHp = el('div', 'hud-world-hp hide', root);
    this.worldHpFill = el('i', '', this.worldHp);
    this.worldWarning = el('span', 'hud-world-warn hide', this.worldHp, 'CRITICAL');
    this.portalHp.length = 0;
    for (let i = 0; i < PORTAL_HP_POOL; i++) {
      const node = el('div', 'hud-portal-hp hide', root);
      const fill = el('i', '', node);
      const num = el('span', 'num', node, '');
      this.portalHp.push({ node, fill, num });
    }
    this.weaponName = el('div', 'hud-weapon-name', left, 'Apprentice Wand');
    this.weaponBar = el('div', 'hud-bar weapon', left);
    this.weaponFill = el('i', '', this.weaponBar);
    this.buffs = el('div', 'hud-buffs', left);
    this.buffShield = el('div', 'hud-buff shield hide', this.buffs);
    const shieldRow = el('div', 'hud-buff-row', this.buffShield);
    el('span', 'hud-buff-name', shieldRow, 'WARD');
    this.buffShieldTime = el('span', 'hud-buff-time', shieldRow, '0.0');
    const shieldBar = el('div', 'hud-bar shield', this.buffShield);
    this.buffShieldFill = el('i', '', shieldBar);
    this.buffSwift = el('div', 'hud-buff swift hide', this.buffs);
    const swiftRow = el('div', 'hud-buff-row', this.buffSwift);
    el('span', 'hud-buff-name', swiftRow, 'GALE');
    this.buffSwiftTime = el('span', 'hud-buff-time', swiftRow, '0.0');
    const swiftBar = el('div', 'hud-bar swift', this.buffSwift);
    this.buffSwiftFill = el('i', '', swiftBar);

    const mid = el('div', 'hud-block hud-room', top);
    const arenaLabel = el('div', 'hud-label', mid, 'Arena');
    this.tierTag = el('span', 'hud-tier', arenaLabel, difficulty().name);
    this.roomValue = el('div', 'value', mid, 'ROOM 1');
    this.roomSub = el('div', 'sub', mid, '');

    const right = el('div', 'hud-block hud-score', top);
    el('div', 'hud-label', right, 'Score');
    this.scoreValue = el('div', 'value', right, '0');
    this.comboBox = el('div', 'hud-combo', right);
    this.comboValue = el('span', 'count', this.comboBox, '0');
    el('span', 'word', this.comboBox, 'COMBO');
    this.comboMult = el('span', 'mult', this.comboBox, 'x1');

    const pauseBtn = el('button', 'hud-pause-btn', top);
    pauseBtn.type = 'button';
    pauseBtn.setAttribute('aria-label', 'Pause Game');
    el('span', 'hud-pause-bar', pauseBtn);
    el('span', 'hud-pause-bar', pauseBtn);
    pauseBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onPause?.();
    });

    this.warning = el('div', 'hud-warning', root, 'THE WYRM IS COMING');
    this.stinger = el('div', 'hud-stinger', root, '');

    this.boss = el('div', 'hud-boss', root);
    this.bossName = el('div', 'name', this.boss, '');
    this.bossTitle = el('div', 'title', this.boss, '');
    const bossTrack = el('div', 'track', this.boss);
    this.bossFill = el('i', 'fill', bossTrack);
    this.bossGhost = el('i', 'ghost', bossTrack);
    const bossSegs = el('div', 'segs', bossTrack);
    for (let i = 0; i < BOSS_SEGMENTS; i++) el('i', '', bossSegs);
    this.bossHp = el('div', 'hp', this.boss, '');

    this.bossCard = el('div', 'hud-bosscard', root);
    this.bossCardKicker = el('div', 'kicker', this.bossCard, '');
    this.bossCardName = el('div', 'name', this.bossCard, '');
    this.bossCardTitle = el('div', 'title', this.bossCard, '');

    this.caption = el('div', 'hud-caption', root);
    el('span', 'who', this.caption, 'ARCHMAGE VAELEN');
    this.captionText = el('span', '', this.caption, '');

    this.center = el('div', 'hud-center', root);
    el('div', 'hud-continue-kicker', this.center, 'INSERT COIN');
    this.centerTitle = el('h1', '', this.center, '');
    this.continueClock = el('div', 'hud-continue-clock', this.center, '9');
    this.continueMeter = el('div', 'hud-continue-meter', this.center);
    this.continueMeterFill = el('i', '', this.continueMeter);
    this.centerSub = el('p', '', this.center, '');
    this.centerDoors = el('div', 'hud-doors', this.center);
    const actions = el('div', 'hud-center-actions', this.center);
    this.centerButton = el('button', '', actions, 'RE-ENTER THE ARENA');
    this.centerButton.type = 'button';
    this.centerButton.style.display = 'none';
    this.centerButton.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.continueOpen) {
        this.acceptContinue();
        return;
      }
      this.onRestart?.();
    });
    this.centerTitleBtn = el('button', 'secondary', actions, 'TITLE SCREEN');
    this.centerTitleBtn.type = 'button';
    this.centerTitleBtn.style.display = 'none';
    this.centerTitleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.continueOpen) {
        this.rejectContinue();
        return;
      }
      this.onTitle?.();
    });

    this.minimap = el('div', 'hud-minimap', root);
    this.minimapLabel = el('div', 'hud-label', this.minimap, 'FLOOR 1');
    this.minimapTheme = el('div', 'hud-theme', this.minimap, 'THE MARBLE COLOSSEUM');
    this.minimapRule = el('div', 'hud-rule', this.minimap, '');
    this.minimapGrid = el('div', 'grid', this.minimap);
    this.rebuildMinimapGrid();

    const bottom = el('div', 'hud-bottom', root);
    this.bottomLeft = el('div', '', bottom, '');
    el('div', 'hud-controls-hint', bottom, 'WASD MOVE  |  DOUBLE-TAP DASH  |  MOUSE AIM  |  LMB FIRE  |  Esc/P PAUSE  |  R RESTART');
    el('div', 'hud-controls-hint-touch', bottom, 'LEFT STICK MOVE  |  DASH BUTTON OR DOUBLE-FLICK  |  RIGHT STICK AIM & CAST  |  PAUSE');
  }

  /** Keep ELIMINATED hidden while the death cinematic plays. */
  holdGameOverOverlay(): void {
    this.holdGameOver = true;
    this.hideCenter();
  }

  /** Clear the hold without showing the panel (restart / title mid-cinematic). */
  clearGameOverHold(): void {
    this.holdGameOver = false;
  }

  /** Show the existing game-over or victory panel after a cinematic. */
  releaseGameOverOverlay(): void {
    this.holdGameOver = false;
    if (this.ctx.room.phase === RoomPhase.GameOver || this.ctx.room.phase === RoomPhase.Victory) {
      this.onPhaseChange(this.ctx.room.phase);
    }
  }

  /** Hide the room-clear / game-over center panel (e.g. when returning to title). */
  hideCenter(): void {
    this.teardownContinue();
    this.continueOpen = false;
    this.center.classList.remove('show', 'blocking', 'continue-panel', 'urgent');
    this.centerTitle.textContent = '';
    this.centerTitle.className = '';
    this.centerSub.textContent = '';
    this.centerDoors.replaceChildren();
    this.centerButton.style.display = 'none';
    this.centerButton.textContent = 'RE-ENTER THE ARENA';
    this.centerButton.tabIndex = 0;
    this.centerTitleBtn.style.display = 'none';
    this.centerTitleBtn.textContent = 'TITLE SCREEN';
    this.centerTitleBtn.tabIndex = 0;
    this.lastDoorSig = '';
    this.clearStingers();
  }

  /** Arcade CONTINUE? with a visible countdown. Player must confirm; held fire does not. */
  showContinuePrompt(remaining: number): void {
    this.teardownContinue();
    this.continueOpen = true;
    this.continueArmed = false;
    this.continueAccepted = false;
    this.clearStingers();
    this.centerTitle.textContent = 'CONTINUE?';
    this.centerTitle.className = 'continue';
    this.centerSub.textContent = continueCopy(remaining);
    this.centerDoors.replaceChildren();
    this.centerButton.textContent = 'CONTINUE';
    this.centerButton.style.display = '';
    this.centerButton.tabIndex = -1;
    this.centerTitleBtn.textContent = 'GIVE UP';
    this.centerTitleBtn.style.display = '';
    this.centerTitleBtn.tabIndex = -1;
    this.center.classList.add('show', 'blocking', 'continue-panel');
    this.center.classList.remove('urgent');
    this.paintContinueClock(CONTINUE_MS);
    this.centerButton.blur();
    this.centerTitleBtn.blur();
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();

    const heldCodes = new Set<string>();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.rejectContinue();
        return;
      }
      if (e.code !== 'Enter' && e.code !== 'Space') return;
      e.preventDefault();
      e.stopPropagation();
      if (!this.continueArmed) {
        heldCodes.add(e.code);
        return;
      }
      if (heldCodes.has(e.code)) return;
      this.acceptContinue();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Enter' || e.code === 'Space') heldCodes.delete(e.code);
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    window.addEventListener('keyup', onKeyUp, { capture: true });

    const started = performance.now();
    let padWasUp = false;
    let padRaf = 0;
    const tick = (now: number): void => {
      if (!this.continueOpen || this.continueAccepted) return;
      const left = Math.max(0, CONTINUE_MS - (now - started));
      this.paintContinueClock(left);
      if (left <= 0) {
        this.rejectContinue(true);
        return;
      }
      const pads = navigator.getGamepads?.() ?? [];
      let held = false;
      for (let i = 0; i < pads.length; i++) {
        if (pads[i]?.buttons[0]?.pressed) held = true;
      }
      if (!held) padWasUp = true;
      else if (padWasUp && this.continueArmed) {
        this.acceptContinue();
        return;
      }
      padRaf = requestAnimationFrame(tick);
    };
    padRaf = requestAnimationFrame(tick);

    this.unbindContinue = () => {
      window.removeEventListener('keydown', onKeyDown, { capture: true });
      window.removeEventListener('keyup', onKeyUp, { capture: true });
      cancelAnimationFrame(padRaf);
    };

    this.delayContinue(CONTINUE_ARM_MS, () => {
      this.continueArmed = true;
    });
  }

  /** Drop the CONTINUE? panel without spending / reviving (title / restart). */
  hideContinue(): void {
    if (!this.continueOpen) return;
    this.hideCenter();
  }

  private delayContinue(ms: number, fn: () => void): void {
    const id = window.setTimeout(() => {
      this.continueTimers.delete(id);
      fn();
    }, ms);
    this.continueTimers.add(id);
  }

  private teardownContinue(): void {
    for (const id of this.continueTimers) window.clearTimeout(id);
    this.continueTimers.clear();
    this.unbindContinue();
    this.unbindContinue = () => {};
    this.continueArmed = false;
    this.continueLastSec = -1;
  }

  private paintContinueClock(leftMs: number): void {
    const secs = Math.max(0, Math.ceil(leftMs / 1000));
    this.continueMeterFill.style.transform = `scaleX(${Math.max(0, leftMs / CONTINUE_MS)})`;
    this.center.classList.toggle('urgent', secs > 0 && secs <= 3);
    if (secs === this.continueLastSec) return;
    this.continueLastSec = secs;
    this.continueClock.textContent = String(secs);
    this.continueClock.classList.remove('punch');
    void this.continueClock.offsetWidth;
    this.continueClock.classList.add('punch');
  }

  private acceptContinue(): void {
    if (!this.continueOpen || !this.continueArmed || this.continueAccepted) return;
    this.continueAccepted = true;
    const remaining = this.ctx.continues;
    this.hideCenter();
    if (!Number.isFinite(remaining)) this.showStinger('GET UP', 'prize', 4);
    else this.showStinger(remaining <= 1 ? 'LAST LIFE' : `${remaining - 1} LEFT`, remaining <= 1 ? 'danger' : 'prize', 4);
    this.onContinue?.();
  }

  private rejectContinue(force = false): void {
    if (!this.continueOpen || this.continueAccepted) return;
    if (!force && !this.continueArmed) return;
    this.continueAccepted = true;
    this.hideCenter();
    this.onContinueExpire?.();
  }

  /** Show an announcer caption. Called by the announcer engine when a line plays. */
  showCaption(text: string, durationMs: number): void {
    this.captionText.textContent = text;
    this.caption.classList.add('show');
    this.captionUntil = performance.now() + durationMs;
  }

  /** Big center callout, Smash TV style. One on screen; extras wait their turn. */
  showStinger(text: string, kind = '', priority = 2): void {
    if (this.centerOwnsScreen() || this.bossCardOwnsScreen()) {
      this.queueStinger(text, kind, priority);
      return;
    }
    const now = performance.now();
    if (this.stingerUntil && now < this.stingerUntil) {
      if (priority > this.stingerPriority) {
        this.playStinger(text, kind, priority, now);
        return;
      }
      this.queueStinger(text, kind, priority);
      return;
    }
    this.playStinger(text, kind, priority, now);
  }

  private queueStinger(text: string, kind: string, priority: number): void {
    if (this.stinger.textContent === text && this.stingerUntil) return;
    for (let i = 0; i < this.stingerQueue.length; i++) {
      if (this.stingerQueue[i].text === text) {
        if (priority > this.stingerQueue[i].priority) this.stingerQueue[i].priority = priority;
        return;
      }
    }
    this.stingerQueue.push({ text, kind, priority });
    this.stingerQueue.sort((a, b) => b.priority - a.priority);
    if (this.stingerQueue.length > STINGER_QUEUE_CAP) this.stingerQueue.length = STINGER_QUEUE_CAP;
  }

  private playStinger(text: string, kind: string, priority: number, now: number): void {
    this.stinger.textContent = text;
    this.stinger.className = 'hud-stinger';
    void this.stinger.offsetWidth;
    this.stinger.className = `hud-stinger show ${kind}`;
    this.stingerUntil = now + STINGER_MS;
    this.stingerPriority = priority;
  }

  private pumpStingerQueue(now: number): void {
    if (this.centerOwnsScreen() || this.bossCardOwnsScreen()) return;
    const next = this.stingerQueue.shift();
    if (next) this.playStinger(next.text, next.kind, next.priority, now);
  }

  private clearStingers(): void {
    this.stingerQueue.length = 0;
    this.stingerUntil = 0;
    this.stingerPriority = 0;
    this.beginStingerAt = 0;
    this.stinger.className = 'hud-stinger';
    this.stinger.textContent = '';
  }

  private centerOwnsScreen(): boolean {
    return this.center.classList.contains('show');
  }

  private bossCardOwnsScreen(): boolean {
    return this.bossCard.classList.contains('show');
  }

  update(dt: number): void {
    const ctx = this.ctx;
    const now = performance.now();
    this.consumeEvents(now);
    this.updatePlayer();
    this.updatePortalHp();
    this.updateBoss(dt);
    this.updateRoom();
    this.updateCombo();
    this.updateMinimap();
    this.updatePopups(now);
    this.updateFlash(dt);

    // Rolling score: chase the real value so big hauls tick up visibly.
    if (this.shownScore !== ctx.score) {
      const diff = ctx.score - this.shownScore;
      const step = Math.abs(diff) < 50 ? diff : Math.sign(diff) * Math.max(25, Math.ceil(Math.abs(diff) * Math.min(1, dt * 12)));
      this.shownScore += step;
      if ((diff > 0 && this.shownScore > ctx.score) || (diff < 0 && this.shownScore < ctx.score)) this.shownScore = ctx.score;
      this.scoreValue.textContent = this.shownScore.toLocaleString('en-US');
    }
    if (this.captionUntil && now > this.captionUntil) {
      this.caption.classList.remove('show');
      this.captionUntil = 0;
    }
    if (this.hurtUntil && now > this.hurtUntil) {
      this.vignette.classList.remove('hurt');
      this.hurtUntil = 0;
    }
    if (this.stingerUntil && now > this.stingerUntil) {
      this.stinger.classList.remove('show');
      this.stingerUntil = 0;
      this.stingerPriority = 0;
      this.pumpStingerQueue(now);
    }
    // Timed warnings (floor rule blurb) clear on their own; alarms stay until the room says so.
    if (this.warningUntil && now > this.warningUntil) {
      this.warningUntil = 0;
      if (!this.vignette.classList.contains('alarm')) this.warning.classList.remove('show');
    }
    if (this.bossCardUntil && now > this.bossCardUntil) {
      this.bossCard.classList.remove('show', 'out');
      this.bossCardUntil = 0;
      this.pumpStingerQueue(now);
    }
    // The rune build pulses the frame; the pulse speeds up as the boss gets close.
    const building = ctx.room.bossState === BossState.Incoming && ctx.room.phase === RoomPhase.Fight;
    this.vignette.classList.toggle('build', building);
    if (building) this.vignette.style.setProperty('--build', ctx.room.bossBuild.toFixed(3));
    if (this.beginStingerAt && now >= this.beginStingerAt) {
      this.beginStingerAt = 0;
      this.showStinger(floorThemeDef(this.ctx.floor.floor).title, 'opening', 4);
    }
    this.bottomLeft.textContent = `FIENDS ${ctx.pools.count(PoolKind.Imp)}  PACK ${smallFryPackName(ctx.room.featured)}  BRUTES ${ctx.pools.count(PoolKind.Golem)}  PORTALS ${ctx.room.spawnersAlive}`;
  }

  private consumeEvents(now: number): void {
    const events = this.ctx.events;
    for (let i = 0; i < events.length; i++) {
      const e = events.peekAt(i);
      switch (e.type) {
        case GameEvent.PlayerHit:
          this.vignette.classList.add('hurt');
          this.hurtUntil = now + 140;
          break;
        case GameEvent.StallWarning:
          this.warning.textContent = e.a === 1 ? 'STOP HIDING. THE WYRM IS COMING' : 'TIME IS UP. THE WYRM IS COMING';
          this.warning.classList.add('show');
          this.vignette.classList.add('alarm');
          break;
        case GameEvent.DragonSpawn:
          this.warning.textContent = 'CINDERWYRM HAS ARRIVED';
          this.showStinger('CINDERWYRM!', 'danger', 5);
          break;
        case GameEvent.BossIncoming: {
          // Title card slams in while the rune circle charges; it holds through the entrance.
          this.clearStingers();
          const def = bossDef(e.a);
          this.bossCardKicker.textContent = def.archmage
            ? isFinalFloor(this.ctx.floor.floor)
              ? 'FINAL ARCHMAGE'
              : `FLOOR ${this.ctx.floor.floor} ARCHMAGE`
            : `CHAMPION OF ROOM ${e.b}`;
          this.bossCardName.textContent = def.name.toUpperCase();
          this.bossCardTitle.textContent = def.title;
          this.bossCard.style.setProperty('--boss-glow', `#${def.glowTint.toString(16).padStart(6, '0')}`);
          this.bossCard.style.setProperty('--boss-bolt', `#${def.boltTint.toString(16).padStart(6, '0')}`);
          this.bossCard.className = 'hud-bosscard';
          void this.bossCard.offsetWidth;
          this.bossCard.classList.add('show');
          this.bossCardUntil = now + 3600;
          this.warning.textContent = `${def.title} INCOMING`;
          this.warning.classList.add('show');
          this.vignette.classList.add('alarm');
          break;
        }
        case GameEvent.BossSpawn: {
          const def = bossDef(e.b);
          this.warning.textContent = `${def.name.toUpperCase()} HAS ARRIVED`;
          this.warning.classList.add('show');
          break;
        }
        case GameEvent.BossLanded:
          this.bossCard.classList.add('out');
          this.bossCardUntil = now + 450;
          this.showStinger('FIGHT!', 'danger', 4);
          break;
        case GameEvent.BossHalf:
          this.showStinger('HALF DEAD!', 'danger', 4);
          break;
        case GameEvent.BossDeath:
          this.warning.classList.remove('show');
          this.vignette.classList.remove('alarm');
          break;
        case GameEvent.BossDown:
          this.showStinger('BOSS DOWN!', 'clear', 3);
          break;
        case GameEvent.SpawnerDestroyed:
          // Last portal hands the screen to the boss title card. Intermediate kills still call.
          if (e.a !== 0) this.showStinger('PORTAL DOWN!', 'portal', 2);
          break;
        case GameEvent.KillStreak:
          this.showStinger('TOTAL MAYHEM!', 'mayhem', 2);
          break;
        case GameEvent.PrizeWave:
        case GameEvent.FloorCleared:
          // ROOM CLEARED / FLOOR CLEARED already own the middle of the screen.
          break;
        case GameEvent.RunWon:
          this.clearStingers();
          this.bossCard.classList.remove('show', 'out');
          this.bossCardUntil = 0;
          this.warning.classList.remove('show');
          this.vignette.classList.remove('alarm');
          break;
        case GameEvent.Overcharge:
          this.showStinger('OVERCHARGE!', 'prize', 2);
          break;
        case GameEvent.PickupShield:
          this.showStinger('RUNE WARD!', 'portal', 2);
          break;
        case GameEvent.PickupSwift:
          this.showStinger('GALE STEP!', 'prize', 2);
          break;
        case GameEvent.NewThreat:
          this.showStinger(`NEW FIEND: ${smallFryPackName(e.a)}!`, 'portal', 3);
          break;
        case GameEvent.FloorModifier: {
          const rule = floorMod(e.a);
          this.showStinger(`FLOOR RULE: ${rule.name}`, 'rule', 3);
          this.warning.textContent = rule.blurb.toUpperCase();
          this.warning.classList.add('show');
          this.warningUntil = now + 3200;
          break;
        }
        case GameEvent.ArenaStart:
          this.showStinger("CHAMPION'S RING!", 'danger', 4);
          break;
        case GameEvent.SanctumRest:
          this.showStinger('SANCTUM. BREATHE.', 'clear', 3);
          break;
        case GameEvent.MimicWake:
          this.showStinger('MIMIC!', 'danger', 5);
          break;
        case GameEvent.MimicSlain:
          this.showStinger('CHEST BUSTED!', 'prize', 3);
          break;
        case GameEvent.RoomCleared:
        case GameEvent.RoomNext:
          this.warning.classList.remove('show');
          this.vignette.classList.remove('alarm');
          break;
        case GameEvent.RoomEnter:
          this.warning.classList.remove('show');
          this.vignette.classList.remove('alarm');
          if (this.ctx.room.roomIndex === 1) {
            this.lastThemeFloor = 0;
            this.beginStingerAt = now + 850;
          } else if (this.ctx.floor.floor !== this.lastThemeFloor) {
            this.showStinger(floorThemeDef(this.ctx.floor.floor).title, 'opening', 4);
          }
          this.lastThemeFloor = this.ctx.floor.floor;
          break;
        default:
          break;
      }
    }
  }

  private updatePlayer(): void {
    const players = query(this.ctx.world, [Player, Health, Weapon]);
    if (players.length === 0) return;
    const eid = players[0];
    const hp = Math.max(0, Health.hp[eid]);
    const max = Health.max[eid] || 1;
    const frac = Math.max(0, Math.min(1, hp / max));
    const low = frac <= Config.player.lowPowerFraction;
    const critical = frac <= Config.player.criticalPowerFraction;
    const alive = Player.dead[eid] === 0;

    this.vignette.classList.toggle('low-power', alive && low && !critical);
    this.vignette.classList.toggle('critical-power', alive && critical);

    if (hp !== this.displayHp) {
      this.displayHp = hp;
      const lit = Math.ceil(frac * HEALTH_SEGMENTS);
      for (let i = 0; i < HEALTH_SEGMENTS; i++) {
        const seg = this.healthSegs[i];
        seg.className = i < lit ? (critical ? 'on critical' : low ? 'on low' : 'on') : '';
      }
      this.healthText.textContent = `${Math.ceil(hp)} / ${max}`;
    }
    this.healthFill.style.transform = `scaleX(${frac})`;
    this.healthFill.classList.toggle('low', low && !critical);
    this.healthFill.classList.toggle('critical', critical);
    this.worldHpFill.style.transform = `scaleX(${frac})`;
    this.worldHp.classList.toggle('low', low && !critical);
    this.worldHp.classList.toggle('critical', critical);

    const showWarn = alive && low;
    this.powerWarning.classList.toggle('hide', !showWarn);
    this.powerWarning.classList.toggle('critical', critical);
    this.powerWarning.textContent = critical ? 'POWER CRITICAL' : 'LOW POWER';

    this.worldWarning.classList.toggle('hide', !critical);
    this.worldWarning.classList.toggle('critical', critical);
    this.worldWarning.textContent = 'CRITICAL';

    this.worldHp.classList.toggle('hide', !alive || frac >= 0.995);
    if (alive && this.projector) {
      this.projector(Position.x[eid], Position.y[eid] + 1.15, this.screen);
      this.worldHp.style.transform = `translate(-50%, -100%) translate(${this.screen[0]}px, ${this.screen[1]}px)`;
    }

    const def = weaponDef(Weapon.typeId[eid]);
    const oc = Weapon.overcharge[eid] > 0;
    const charge = def.id === WeaponId.Cataclysm ? `${def.name}  //  ONE CHARGE` : def.name;
    const label = oc ? `${def.name}  //  OVERCHARGE ${Weapon.overcharge[eid].toFixed(1)}s` : charge;
    if (this.weaponName.textContent !== label) this.weaponName.textContent = label;
    this.weaponName.classList.toggle('overcharge', oc);
    this.weaponBar.classList.toggle('overcharge', oc);
    if (def.duration > 0) {
      this.weaponBar.style.visibility = 'visible';
      this.weaponFill.style.transform = `scaleX(${Math.max(0, Weapon.timeLeft[eid] / def.duration)})`;
    } else {
      this.weaponBar.style.visibility = 'hidden';
    }

    const wardOn = Player.offhandTime[eid] > 0;
    this.buffShield.classList.toggle('hide', !wardOn);
    if (wardOn) {
      const left = Player.offhandTime[eid];
      this.buffShieldTime.textContent = left.toFixed(1);
      this.buffShieldFill.style.transform = `scaleX(${Math.max(0, left / Config.pickups.shield.duration)})`;
      this.buffShield.classList.toggle('expiring', left <= 3);
    }
    const galeOn = Player.swiftTime[eid] > 0;
    this.buffSwift.classList.toggle('hide', !galeOn);
    if (galeOn) {
      const left = Player.swiftTime[eid];
      this.buffSwiftTime.textContent = left.toFixed(1);
      this.buffSwiftFill.style.transform = `scaleX(${Math.max(0, left / Config.pickups.swift.duration)})`;
      this.buffSwift.classList.toggle('expiring', left <= 3);
    }
    this.buffs.classList.toggle('hide', !wardOn && !galeOn);

    const lives = this.ctx.continues;
    if (lives !== this.lastContinues) {
      this.lastContinues = lives;
      this.continuesTag.textContent = `CONT ${formatContinues(lives)}`;
      this.continuesTag.classList.toggle('last', lives === 0);
    }
  }

  /** Slim HP chips above each live portal. Numbers only come up once a gate is wounded. */
  private updatePortalHp(): void {
    const ctx = this.ctx;
    const bars = this.portalHp;
    if (
      !this.projector ||
      ctx.room.phase === RoomPhase.Transition ||
      ctx.room.phase === RoomPhase.GameOver ||
      ctx.room.phase === RoomPhase.Victory
    ) {
      for (let i = 0; i < bars.length; i++) bars[i].node.classList.add('hide');
      return;
    }
    const spawners = query(ctx.world, [Spawner, Health, Position]);
    let used = 0;
    for (let i = 0; i < spawners.length && used < bars.length; i++) {
      const eid = spawners[i];
      if (Dying.flag[eid] || Health.hp[eid] <= 0 || Health.max[eid] <= 0) continue;
      const hp = Health.hp[eid];
      const max = Health.max[eid];
      const frac = Math.max(0, Math.min(1, hp / max));
      const bar = bars[used++];
      this.projector(Position.x[eid], Position.y[eid] + 1.85, this.screen);
      bar.node.style.transform = `translate(-50%, -100%) translate(${this.screen[0]}px, ${this.screen[1]}px)`;
      bar.fill.style.transform = `scaleX(${frac})`;
      const label = `${Math.ceil(hp)} / ${max}`;
      if (bar.num.textContent !== label) bar.num.textContent = label;
      bar.node.classList.toggle('wounded', frac <= 0.6 && frac > 0.3);
      bar.node.classList.toggle('critical', frac <= 0.3);
      bar.node.classList.remove('hide');
    }
    for (let i = used; i < bars.length; i++) bars[i].node.classList.add('hide');
  }

  /** Top-center boss bar: name, title, segmented health with a lagging ghost fill. */
  private updateBoss(dt: number): void {
    const room = this.ctx.room;
    const eid = room.bossEid;
    const live = room.bossState !== BossState.None && eid !== -1 && entityExists(this.ctx.world, eid) && hasComponent(this.ctx.world, eid, BossTag) && Health.max[eid] > 0;
    if (!live) {
      if (this.bossKindShown !== -1) {
        this.bossKindShown = -1;
        this.bossShownFrac = -1;
        this.bossGhostFrac = 1;
        this.boss.classList.remove('show', 'arriving', 'dead');
      }
      return;
    }
    if (this.bossKindShown !== room.bossKind) {
      this.bossKindShown = room.bossKind;
      const def = bossDef(room.bossKind);
      this.bossName.textContent = def.name.toUpperCase();
      this.bossTitle.textContent = def.title;
      this.boss.style.setProperty('--boss-glow', `#${def.glowTint.toString(16).padStart(6, '0')}`);
      this.boss.style.setProperty('--boss-bolt', `#${def.boltTint.toString(16).padStart(6, '0')}`);
      this.boss.classList.add('show');
      this.bossGhostFrac = 1;
    }
    const hp = Math.max(0, Health.hp[eid]);
    const frac = Math.min(1, hp / Health.max[eid]);
    const arriving = BossTag.phase[eid] === 0;
    this.boss.classList.toggle('arriving', arriving);
    this.boss.classList.toggle('dead', room.bossState === BossState.Dead);
    // Ghost fill trails the real one so big hits read as a chunk falling off.
    if (this.bossGhostFrac > frac) this.bossGhostFrac = Math.max(frac, this.bossGhostFrac - dt * 0.35);
    this.bossGhost.style.transform = `scaleX(${this.bossGhostFrac})`;
    if (frac !== this.bossShownFrac) {
      this.bossShownFrac = frac;
      this.bossFill.style.transform = `scaleX(${frac})`;
      this.bossHp.textContent = `${Math.ceil(hp)} / ${Health.max[eid]}`;
      this.boss.classList.toggle('enraged', frac <= Config.boss.enrageBelow && frac > 0);
    }
  }

  private updateCombo(): void {
    const combo = this.ctx.combo;
    if (combo.count === this.lastCombo) return;
    const grew = combo.count > this.lastCombo;
    this.lastCombo = combo.count;
    this.comboValue.textContent = String(combo.count);
    this.comboMult.textContent = `x${combo.multiplier}`;
    this.comboBox.classList.toggle('show', combo.count >= 3);
    this.comboBox.classList.toggle('hot', combo.multiplier >= 3);
    if (grew) {
      this.comboBox.classList.remove('bump');
      void this.comboBox.offsetWidth;
      this.comboBox.classList.add('bump');
    }
  }

  private updateRoom(): void {
    const room = this.ctx.room;
    if (room.roomIndex !== this.lastRoom || room.featured !== this.lastFeatured) {
      this.lastRoom = room.roomIndex;
      this.lastFeatured = room.featured;
      this.roomValue.textContent = `ROOM ${room.roomIndex}  ·  ${smallFryPackName(room.featured)}`;
      // The tier is picked on the start screen after the HUD is built, so refresh it per room.
      const tierName = difficulty().name;
      if (this.tierTag.textContent !== tierName) this.tierTag.textContent = tierName;
    }
    const wyrmLeft = Math.max(
      0,
      Math.ceil(Config.dragon.stallSeconds * difficulty().stallSeconds * floorMod(this.ctx.floor.modifier).stallScale - room.fightTime),
    );
    const sub =
      room.phase === RoomPhase.Fight
        ? room.bossState !== BossState.None
          ? room.bossState === BossState.Dead
            ? 'BOSS DOWN'
            : room.bossState === BossState.Incoming
              ? 'BOSS INCOMING'
              : bossDef(room.bossKind).archmage
                ? 'ARCHMAGE'
                : 'CHAMPION'
          : room.dragonWarned
            ? 'WYRM INBOUND'
            : wyrmLeft <= 12
              ? `WYRM ${wyrmLeft}s`
              : ''
        : room.phase === RoomPhase.Clear
          ? 'PICK A DOOR'
          : room.phase === RoomPhase.Enter
            ? 'GATES LOCKED'
            : room.phase === RoomPhase.Transition
              ? 'NEXT ROOM'
              : room.phase === RoomPhase.Victory
                ? 'ARENA CHAMPION'
                : '';
    if (this.roomSub.textContent !== sub) this.roomSub.textContent = sub;

    if (room.phase !== this.lastPhase) {
      this.lastPhase = room.phase;
      this.onPhaseChange(room.phase);
    }
    if (room.phase === RoomPhase.Clear) {
      const sig = Array.from(room.doorKinds).join(',');
      if (sig !== this.lastDoorSig) {
        this.lastDoorSig = sig;
        this.centerDoors.replaceChildren();
        for (let g = 0; g < 4; g++) {
          const kind = room.doorKinds[g] as DoorKind;
          if (kind === DoorKind.Sealed) continue;
          const door = el('div', 'hud-door', this.centerDoors, `${GATE_LABELS[g]}  ${DOOR_NAMES[kind]}`);
          door.dataset.kind = String(kind);
        }
      }
    }
  }

  /** Floors change shape: rebuild the cell grid whenever cols/rows move. */
  private rebuildMinimapGrid(): void {
    const floor = this.ctx.floor;
    if (floor.cols === this.minimapCols && floor.rows === this.minimapRows) return;
    this.minimapCols = floor.cols;
    this.minimapRows = floor.rows;
    this.minimapGrid.replaceChildren();
    this.minimapCells.length = 0;
    this.minimapGrid.style.gridTemplateColumns = `repeat(${floor.cols}, 1fr)`;
    this.minimapGrid.dataset.cols = String(floor.cols);
    for (let i = 0; i < floor.cols * floor.rows; i++) this.minimapCells.push(el('div', 'cell', this.minimapGrid));
    this.lastMapSig = '';
  }

  private updateMinimap(): void {
    const floor = this.ctx.floor;
    const room = this.ctx.room;
    const zoom = room.phase === RoomPhase.Transition;
    this.minimap.classList.toggle('zoom', zoom);
    this.rebuildMinimapGrid();
    // Signature covers everything that changes a cell's class.
    const cells = floor.cellCount;
    let sig = `${floor.floor}:${floor.current}:${zoom ? 1 : 0}:${floor.modifier}:`;
    for (let i = 0; i < cells; i++) sig += floor.kinds[i] + (floor.visited[i] ? 'v' : '') + (floor.cleared[i] ? 'c' : '');
    if (sig === this.lastMapSig) return;
    this.lastMapSig = sig;
    const theme = floorThemeDef(floor.floor);
    this.minimapLabel.textContent = zoom
      ? `FLOOR ${floor.floor}  //  NEXT ROOM`
      : isFinalFloor(floor.floor)
        ? `FLOOR ${floor.floor}  //  FINALE`
        : `FLOOR ${floor.floor}  //  ${theme.kicker}`;
    this.minimapTheme.textContent = theme.title;
    this.minimap.style.setProperty('--floor-accent', hexCss(theme.trim));
    const rule = floorMod(floor.modifier);
    this.minimapRule.textContent = floor.modifier === FloorModifier.None ? '' : `RULE: ${rule.name}`;
    this.minimapRule.classList.toggle('show', floor.modifier !== FloorModifier.None);
    const cur = floor.current;
    for (let i = 0; i < this.minimapCells.length; i++) {
      const cell = this.minimapCells[i];
      const kind = floor.kinds[i] as DoorKind;
      const col = i % floor.cols;
      const row = (i - col) / floor.cols;
      const adjacent = Math.abs(col - floor.col) + Math.abs(row - floor.row) === 1;
      let cls = 'cell';
      if (floor.visited[i]) cls += ' visited';
      if (floor.cleared[i]) cls += ' cleared';
      if (adjacent || floor.visited[i]) cls += ` k${kind}`;
      if (adjacent) cls += ' adjacent';
      if (i === cur) cls += ' current';
      if (kind === DoorKind.Exit) cls += ' exit';
      cell.className = cls;
    }
  }

  private updatePopups(now: number): void {
    const fx = this.ctx.fx;
    for (let i = 0; i < fx.popupCount; i++) {
      const o = i * POPUP_STRIDE;
      this.spawnPopup(fx.popups[o] as PopupKind, fx.popups[o + 1], fx.popups[o + 2], fx.popups[o + 3], now);
    }
    fx.popupCount = 0;
    if (!this.projector) return;
    for (let i = 0; i < this.popups.length; i++) {
      const p = this.popups[i];
      if (!p.until) continue;
      if (now > p.until) {
        p.until = 0;
        p.node.className = 'hud-popup';
        continue;
      }
      const t = 1 - (p.until - now) / POPUP_LIFE_MS;
      this.projector(p.x, p.y + t * 0.9, this.screen);
      p.node.style.transform = `translate(-50%, -50%) translate(${this.screen[0]}px, ${this.screen[1]}px) scale(${1 + (1 - t) * 0.25})`;
      p.node.style.opacity = String(t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3);
    }
  }

  private spawnPopup(kind: PopupKind, value: number, x: number, y: number, now: number): void {
    // Take the free slot or the oldest.
    let slot = this.popups[0];
    for (let i = 0; i < this.popups.length; i++) {
      const p = this.popups[i];
      if (!p.until) {
        slot = p;
        break;
      }
      if (p.until < slot.until) slot = p;
    }
    slot.until = now + POPUP_LIFE_MS;
    slot.x = x;
    slot.y = y;
    switch (kind) {
      case PopupKind.Combo:
        slot.node.textContent = `x${value} COMBO`;
        slot.node.className = 'hud-popup show combo';
        break;
      case PopupKind.Prize:
        slot.node.textContent = `+${value.toLocaleString('en-US')}`;
        slot.node.className = 'hud-popup show prize';
        break;
      case PopupKind.Heal:
        slot.node.textContent = `+${value} HP`;
        slot.node.className = 'hud-popup show heal';
        break;
      default:
        slot.node.textContent = `+${value.toLocaleString('en-US')}`;
        slot.node.className = 'hud-popup show';
        break;
    }
  }

  private updateFlash(dt: number): void {
    const fx = this.ctx.fx;
    if (fx.flash <= 0.001) {
      if (this.flash.style.opacity !== '0') this.flash.style.opacity = '0';
      return;
    }
    this.flash.style.opacity = String(Math.min(1, fx.flash));
    fx.flash = Math.max(0, fx.flash - dt * 4);
  }

  private onPhaseChange(phase: RoomPhase): void {
    const ctx = this.ctx;
    this.center.classList.remove('continue-panel', 'urgent');
    switch (phase) {
      case RoomPhase.Clear:
        this.clearStingers();
        this.centerTitle.textContent = ctx.room.door === DoorKind.Exit ? 'FLOOR CLEARED' : 'ROOM CLEARED';
        this.centerTitle.className = 'clear';
        this.centerSub.textContent = `Cleared in ${Math.round(ctx.room.fightTime)}s. Walk into a glowing gate.`;
        this.centerButton.style.display = 'none';
        this.centerTitleBtn.style.display = 'none';
        this.center.classList.add('show');
        this.center.classList.remove('blocking');
        break;
      case RoomPhase.GameOver:
        this.clearStingers();
        if (this.holdGameOver) {
          this.center.classList.remove('show', 'blocking');
          this.centerButton.style.display = 'none';
          this.centerTitleBtn.style.display = 'none';
          break;
        }
        this.centerTitle.textContent = 'ELIMINATED';
        this.centerTitle.className = '';
        this.centerSub.textContent = `Score ${ctx.score.toLocaleString('en-US')}  //  ${ctx.kills} kills  //  best combo ${ctx.combo.best}  //  Room ${ctx.room.roomIndex}`;
        this.centerDoors.replaceChildren();
        this.centerButton.textContent = 'RE-ENTER THE ARENA';
        this.centerButton.style.display = '';
        this.centerTitleBtn.style.display = '';
        this.center.classList.add('show', 'blocking');
        break;
      case RoomPhase.Victory:
        this.clearStingers();
        if (this.holdGameOver) {
          this.center.classList.remove('show', 'blocking');
          this.centerButton.style.display = 'none';
          this.centerTitleBtn.style.display = 'none';
          break;
        }
        this.centerTitle.textContent = 'YOU BEAT THE ARENA';
        this.centerTitle.className = 'victory';
        this.centerSub.textContent = `Score ${ctx.score.toLocaleString('en-US')}  //  ${ctx.kills} kills  //  best combo ${ctx.combo.best}  //  Floor ${ctx.floor.floor}  //  Room ${ctx.room.roomIndex}`;
        this.centerDoors.replaceChildren();
        this.centerButton.textContent = 'RE-ENTER THE ARENA';
        this.centerButton.style.display = '';
        this.centerTitleBtn.style.display = '';
        this.center.classList.add('show', 'blocking');
        break;
      default:
        this.center.classList.remove('show', 'blocking');
        this.centerDoors.replaceChildren();
        this.centerButton.style.display = 'none';
        this.centerTitleBtn.style.display = 'none';
        this.lastDoorSig = '';
        break;
    }
  }
}
